import { fetchFresh } from '@/lib/API/fetcher'
import { hasValue, parseNullableId } from '@/lib/helpers/numeric'
import { isSessionOwner } from '@/lib/helpers/session-access'

// P7: the Polar captures the server keeps (Upload resources of sensor_type
// heartrate), as the app lists and labels them. A capture goes to the server
// at its import; the server joins it to its session, or keeps it waiting for
// the rider to choose it in « C'est bien ça ? », the session's settings or the
// page of a MOVE PRO file.

// How far from a session a waiting capture is still offered for it.
export const HEARTRATE_UPLOADS_AROUND_MS = 60 * 60 * 1000

// The user's heart-rate uploads, newest first; `waiting`: only those no session
// took yet. From the network only ([] offline): a stale list would offer
// captures another session already took.
export const fetchHeartrateUploads = async (userId, { waiting = false } = {}) => {
    if (!userId) return []
    const response = await fetchFresh(
        `/users/${userId}/uploads?sensor_type=heartrate&per_page=200${waiting ? '&orphan=1' : ''}`
    )
    return Array.isArray(response?.data) ? response.data : []
}

// The waiting captures a session's cardio list offers: the reader's own, only
// when the reader owns the session. PUT /sessions takes any waiting capture of
// the reader's: an admin editing a rider's session must never attach their own
// heart rate to it.
export const fetchWaitingHeartrateUploadsFor = async (session, userId) =>
    isSessionOwner(session, userId) ? fetchHeartrateUploads(userId, { waiting: true }) : []

// A date as the API sends it (ISO string), a Date, or a timestamp (ms).
const timestamp = (value) => {
    if (!hasValue(value)) return null
    const parsed = typeof value === 'number' ? value : value instanceof Date ? value.getTime() : Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
}

// When something ran, as two timestamps (ms), or null when its start is unknown.
export const interval = (start, end) => {
    const from = timestamp(start)
    if (from === null) return null
    const to = timestamp(end)
    return { start: from, end: to !== null && to >= from ? to : from }
}

export const sessionInterval = (session) => {
    const start = timestamp(session?.start_at)
    const elapsed = Number(session?.elapsed_time)
    const end =
        session?.end_at ?? (start !== null && Number.isFinite(elapsed) && elapsed > 0 ? start + elapsed * 1000 : null)
    return interval(session?.start_at, end)
}

const uploadInterval = (upload) => interval(upload?.start_at, upload?.end_at)

// The captures that ran within `margin` of `around`, the closest start first.
export const uploadsAround = (uploads, around, margin = HEARTRATE_UPLOADS_AROUND_MS) => {
    if (!around) return []
    return (Array.isArray(uploads) ? uploads : [])
        .map((upload) => ({ upload, ran: uploadInterval(upload) }))
        .filter(({ ran }) => ran && ran.start < around.end + margin && ran.end > around.start - margin)
        .sort((a, b) => Math.abs(a.ran.start - around.start) - Math.abs(b.ran.start - around.start))
        .map(({ upload }) => upload)
}

// The capture « Oui » keeps: the session's own, else the closest waiting one
// recorded while it ran (the server leaves it waiting when two sessions
// overlap it, and lets the rider choose).
export const proposedHeartrateUpload = (current, waiting, around) =>
    current || uploadsAround(waiting, around, 0)[0] || null

// What a cardio list offers: the session's own capture, then the waiting ones
// recorded around it.
export const heartrateUploadOptions = (current, waiting, around) => [
    ...(current ? [current] : []),
    ...uploadsAround(waiting, around).filter((upload) => upload.id !== current?.id),
]

export const clockTime = (value, locale) => {
    const at = timestamp(value)
    return at === null ? '' : new Date(at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

// « Polar H10 · 10:31–11:19 »: the device, then when it ran.
export const heartrateUploadLabel = (upload, locale) => {
    if (!upload) return ''
    const from = clockTime(upload.start_at, locale)
    const to = clockTime(upload.end_at, locale)
    const label = [upload.device, from && to ? `${from}–${to}` : from].filter(Boolean).join(' · ')
    return label || upload.filename || `#${upload.id}`
}

const uploadId = (value) => parseNullableId(value) ?? null

// What PUT /sessions/{id} gets for a cardio list: nothing while it shows the
// capture it started from (or « Aucun » when it started from none), else the
// chosen capture, null for « Aucun ».
export const heartrateChoice = (initialId, selectedId) => {
    const chosen = uploadId(selectedId)
    return chosen === uploadId(initialId) ? {} : { heartrate_upload_id: chosen }
}
